import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Layout } from "@/components/Layout";
import ReactGA from 'react-ga4';
import { lazy, Suspense } from "react";
import Index from "./pages/Index";

// Lazy loading components
const SIPCalculator = lazy(() => import("./pages/SIPCalculator"));
const FDCalculator = lazy(() => import("./pages/FDCalculator"));
const CompoundInterestCalculator = lazy(() => import("./pages/CompoundInterestCalculator"));
const PercentageCalculator = lazy(() => import("./pages/PercentageCalculator"));
const GeneralCalculator = lazy(() => import("./pages/GeneralCalculator"));
const AgeCalculator = lazy(() => import("./pages/AgeCalculator"));
const BMICalculator = lazy(() => import("./pages/BMICalculator"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const GSTCalculator = lazy(() => import("./pages/GSTCalculator"));
const MileageCalculator = lazy(() => import("./pages/MileageCalculator"));

const MEASUREMENT_ID = "G-RRXD59JXBD"; 
ReactGA.initialize(MEASUREMENT_ID);

const App = () => (
  <HelmetProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Layout>
          <Suspense fallback={
            <div className="flex min-h-[50vh] items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          }>
              <Routes>
                {/* Main Calculators */}
                <Route path="/" element={<Index canonical="https://calculatorsheet.com" />} />
                <Route path="/sip-calculator" element={<SIPCalculator canonical="https://calculatorsheet.com/sip-calculator" />} />
                <Route path="/fd-calculator" element={<FDCalculator canonical="https://calculatorsheet.com/fd-calculator" />} />
                <Route path="/compound-interest-calculator" element={<CompoundInterestCalculator canonical="https://calculatorsheet.com/compound-interest-calculator" />} />
                <Route path="/percentage-calculator" element={<PercentageCalculator canonical="https://calculatorsheet.com/percentage-calculator" />} />
                <Route path="/calculator" element={<GeneralCalculator canonical="https://calculatorsheet.com/calculator" />} />
                <Route path="/general-calculator" element={<Navigate to="/calculator" replace />} />
                <Route path="/age-calculator" element={<AgeCalculator canonical="https://calculatorsheet.com/age-calculator" />} />
                <Route path="/bmi-calculator" element={<BMICalculator canonical="https://calculatorsheet.com/bmi-calculator" />} />
                <Route path="/gst-calculator" element={<GSTCalculator canonical="https://calculatorsheet.com/gst-calculator" />} />
                <Route path="/mileage-calculator" element={<MileageCalculator canonical="https://calculatorsheet.com/mileage-calculator" />} />

                {/* Legacy route aliases for SEO & backward compatibility */}
                <Route path="/sip" element={<Navigate to="/sip-calculator" replace />} />
                <Route path="/fd" element={<Navigate to="/fd-calculator" replace />} />
                <Route path="/bmi" element={<Navigate to="/bmi-calculator" replace />} />
                <Route path="/compound-interest" element={<Navigate to="/compound-interest-calculator" replace />} />
                <Route path="/percentage" element={<Navigate to="/percentage-calculator" replace />} />

                {/* Trust, Legal & Operational Pages */}
                <Route path="/about" element={<About canonical="https://calculatorsheet.com/about" />} />
                <Route path="/contact" element={<Contact canonical="https://calculatorsheet.com/contact" />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy canonical="https://calculatorsheet.com/privacy-policy" />} />
                <Route path="/terms-conditions" element={<TermsConditions canonical="https://calculatorsheet.com/terms-conditions" />} />
                <Route path="/disclaimer" element={<Disclaimer canonical="https://calculatorsheet.com/disclaimer" />} />

                {/* 404 Catch All */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Layout>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
);

export default App;
